/**
 * Display adapter for the audit log.
 *
 * Raw audit rows are stored immutably and shown one-to-one — every recorded
 * change appears as its own entry. This pure util just reshapes the stored rows
 * into the display shape the page renders (nested actor, time range, id list)
 * and orders them newest-first. No merging, no-op dropping, or field collapsing
 * happens here.
 *
 * No React/Supabase imports — unit-tested in test/unit/utils/auditRollup.test.ts.
 */

export type AuditOperation = "INSERT" | "UPDATE" | "DELETE";

export interface FieldChange {
  old: unknown;
  new: unknown;
}

export interface AuditEntry {
  id: string;
  table_name: string;
  record_id: string;
  operation: AuditOperation;
  actor_id: string | null;
  actor_email: string | null;
  actor_name: string | null;
  record_label: string | null;
  office_id: string | null;
  changes: Record<string, FieldChange>;
  created_at: string; // ISO timestamp
}

export interface RolledUpActor {
  id: string | null;
  email: string | null;
  name: string | null;
}

export interface RolledUpEntry {
  ids: string[]; // raw entry ids backing this display entry (always one now)
  table_name: string;
  record_id: string;
  operation: AuditOperation;
  actor: RolledUpActor;
  record_label: string | null;
  office_id: string | null;
  changes: Record<string, FieldChange>;
  firstAt: string;
  lastAt: string;
}

/**
 * Reshape raw audit entries into the display list, newest-first. Each raw row
 * maps to exactly one display entry — nothing is condensed.
 */
export function toRolledUpEntries(entries: AuditEntry[]): RolledUpEntry[] {
  return [...entries]
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
    .map((e) => ({
      ids: [e.id],
      table_name: e.table_name,
      record_id: e.record_id,
      operation: e.operation,
      actor: { id: e.actor_id, email: e.actor_email, name: e.actor_name },
      record_label: e.record_label,
      office_id: e.office_id,
      changes: e.changes,
      firstAt: e.created_at,
      lastAt: e.created_at,
    }));
}

// An archive/unarchive action records a single `archived` change. Detect it so
// condensing can leave it as its own entry rather than folding it into the item's
// create/edit rows — archiving reads as a distinct action, not a field tweak.
function isArchiveToggle(entry: RolledUpEntry): boolean {
  const keys = Object.keys(entry.changes);
  return keys.length === 1 && keys[0] === "archived";
}

// Fold a same-item burst (newest-first) into one entry. Each field collapses to
// first old → last new. Fields are emitted in `columnOrder` (any not listed keep
// their first-seen order, after the listed ones), so a to-do created then filled
// in reads top-to-bottom in the same order as the To Do List table columns.
function mergeRecordGroup(
  group: RolledUpEntry[],
  columnOrder: string[],
): RolledUpEntry {
  const newest = group[0]!;
  const oldest = group[group.length - 1]!;

  // Walk oldest → newest so first-seen order (the fallback for unlisted fields)
  // is chronological.
  const seenOrder: string[] = [];
  const byField = new Map<string, FieldChange>();
  for (let i = group.length - 1; i >= 0; i--) {
    for (const [field, change] of Object.entries(group[i]!.changes)) {
      const seen = byField.get(field);
      if (seen) seen.new = change.new; // later edit wins for `new`
      else {
        byField.set(field, { old: change.old, new: change.new });
        seenOrder.push(field);
      }
    }
  }
  const rank = (field: string) => {
    const i = columnOrder.indexOf(field);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  // Stable sort keeps unlisted fields in first-seen order at the end.
  const ordered = [...seenOrder].sort((a, b) => rank(a) - rank(b));
  const changes: Record<string, FieldChange> = {};
  for (const field of ordered) changes[field] = byField.get(field)!;

  // Net operation: a trailing deletion reads as a deletion; otherwise a creation
  // anywhere in the burst reads as a creation; else an update.
  const operation: AuditOperation =
    newest.operation === "DELETE"
      ? "DELETE"
      : group.some((e) => e.operation === "INSERT")
        ? "INSERT"
        : newest.operation;

  return {
    ids: group.flatMap((e) => e.ids), // newest-first
    table_name: newest.table_name,
    record_id: newest.record_id,
    operation,
    actor: newest.actor,
    record_label: group.find((e) => e.record_label != null)?.record_label ?? null,
    office_id: newest.office_id,
    changes,
    firstAt: oldest.firstAt,
    lastAt: newest.lastAt,
  };
}

// The To Do List saves each field on its own blur, so editing one item's client
// and task records two separate audit rows. Within an already-bounded burst this
// folds the per-field rows for the same item (record_id) into one entry — so the
// timeline shows "Client → X, Task → Y" together instead of as two near-identical
// lines, with fields ordered to match `columnOrder` (the table's columns). Input
// is newest-first; only the named table condenses, so unrelated rows (different
// items, or other tables sharing the burst) pass through untouched and keep their
// original order. The merged entry keeps the slot of the item's most recent edit.
export function condenseSameRecord(
  entries: RolledUpEntry[],
  table: string,
  columnOrder: string[] = [],
): RolledUpEntry[] {
  const order: (RolledUpEntry | null)[] = [];
  const slot = new Map<string, number>(); // record_id → its reserved index in `order`
  const groups = new Map<string, RolledUpEntry[]>(); // record_id → entries (newest-first)

  for (const e of entries) {
    // Archive/unarchive stays its own entry (still within the same group card).
    if (e.table_name !== table || isArchiveToggle(e)) {
      order.push(e);
      continue;
    }
    const existing = groups.get(e.record_id);
    if (existing) existing.push(e);
    else {
      groups.set(e.record_id, [e]);
      slot.set(e.record_id, order.length);
      order.push(null); // reserve the position of this item's newest edit
    }
  }

  for (const [recordId, group] of groups) {
    order[slot.get(recordId)!] = mergeRecordGroup(group, columnOrder);
  }
  return order as RolledUpEntry[];
}
